import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from 'docx'
import { saveAs } from 'file-saver'
import html2pdf from 'html2pdf.js'

function cleanText(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function cleanList(value) {
  return Array.isArray(value) ? value.map((item) => cleanText(String(item))).filter(Boolean) : []
}

function pushList(lines, title, items) {
  const safeItems = cleanList(items)
  if (!safeItems.length) return
  lines.push('', title)
  safeItems.forEach((item) => lines.push(`- ${item}`))
}

function projectLines(projectSuggestions) {
  if (!Array.isArray(projectSuggestions)) return []
  return projectSuggestions.flatMap((item, index) => {
    const project = item && typeof item === 'object' ? item : {}
    const lines = []
    lines.push(`${index + 1}. ${cleanText(project.title) || '项目经历优化'}`)
    if (cleanText(project.problem)) lines.push(`问题：${cleanText(project.problem)}`)
    if (cleanText(project.improved)) lines.push(`优化表达：${cleanText(project.improved)}`)
    const star = project.starRewrite && typeof project.starRewrite === 'object' ? project.starRewrite : {}
    const starItems = [
      ['S', star.situation],
      ['T', star.task],
      ['A', star.action],
      ['R', star.result],
    ].filter(([, value]) => cleanText(value))
    if (starItems.length) {
      lines.push(`STAR：${starItems.map(([key, value]) => `${key}: ${cleanText(value)}`).join('；')}`)
    }
    return lines
  })
}

export function buildResumeAnalysisText(analysis, meta = {}) {
  const lines = ['AI 简历分析与优化结果']
  if (meta.textLength) lines.push(`已解析 ${meta.textLength} 个字符`)
  if (analysis?.score !== null && analysis?.score !== undefined) {
    lines.push('', `总体评分：${analysis.score}/100`)
  }
  if (cleanText(analysis?.scoreLevel)) lines.push(`评分等级：${cleanText(analysis.scoreLevel)}`)
  if (cleanText(analysis?.scoreReason)) lines.push(`评分解释：${cleanText(analysis.scoreReason)}`)
  if (cleanText(analysis?.summary)) lines.push('', '一句话总结', cleanText(analysis.summary))

  pushList(lines, '当前最大问题', analysis?.keyProblems)
  pushList(lines, '内容完整度建议', analysis?.contentSuggestions)
  pushList(lines, '教育经历建议', analysis?.educationSuggestions)

  const projects = projectLines(analysis?.projectSuggestions)
  if (projects.length) lines.push('', '项目经历优化建议', ...projects)

  pushList(lines, '技能栏表达建议', analysis?.skillSuggestions)

  const jobMatch = analysis?.jobMatch && typeof analysis.jobMatch === 'object' ? analysis.jobMatch : {}
  if (cleanText(jobMatch.targetRole) || cleanText(jobMatch.matchLevel) || cleanList(jobMatch.missingSkills).length) {
    lines.push('', '实习/求职匹配度')
    if (cleanText(jobMatch.targetRole)) lines.push(`目标岗位：${cleanText(jobMatch.targetRole)}`)
    if (cleanText(jobMatch.matchLevel)) lines.push(`匹配度：${cleanText(jobMatch.matchLevel)}`)
    if (cleanList(jobMatch.missingSkills).length) lines.push(`建议补强：${cleanList(jobMatch.missingSkills).join(' / ')}`)
    if (cleanList(jobMatch.riskPoints).length) lines.push(`投递风险：${cleanList(jobMatch.riskPoints).join(' / ')}`)
  }

  pushList(lines, '可直接复制的优化版项目描述', analysis?.copyableBullets)
  pushList(lines, '面试官可能追问的问题', analysis?.interviewQuestions)
  pushList(lines, 'GitHub 项目写进简历建议', analysis?.githubProjectAdvice)
  pushList(lines, '7 天行动计划', analysis?.sevenDayPlan)
  pushList(lines, '下一步行动建议', analysis?.nextActions)

  return lines.join('\n')
}

function textToDocxParagraphs(text) {
  return text.split('\n').map((line) => {
    if (!line.trim()) return new Paragraph({ text: '' })
    if (!line.startsWith('- ') && !line.match(/^\d+\./) && line.length < 32) {
      return new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun({ text: line, bold: true })],
      })
    }
    return new Paragraph({ text: line })
  })
}

export async function downloadResumeAnalysisDocx(analysis, meta = {}) {
  const text = buildResumeAnalysisText(analysis, meta)
  const doc = new Document({
    sections: [{ children: textToDocxParagraphs(text) }],
  })
  const blob = await Packer.toBlob(doc)
  saveAs(blob, 'resume-analysis.docx')
}

export async function downloadResumeAnalysisPdf(analysis, meta = {}) {
  const text = buildResumeAnalysisText(analysis, meta)
  const node = document.createElement('article')
  node.style.cssText = 'width:720px;padding:32px;font-family:Arial,"Microsoft YaHei",sans-serif;line-height:1.65;color:#111827;white-space:pre-wrap;'
  node.textContent = text
  document.body.appendChild(node)
  try {
    await html2pdf()
      .set({
        filename: 'resume-analysis.pdf',
        margin: 10,
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(node)
      .save()
  } finally {
    node.remove()
  }
}

export async function copyResumeAnalysisText(analysis, meta = {}) {
  const text = buildResumeAnalysisText(analysis, meta)
  await navigator.clipboard.writeText(text)
}
